/**
 * TokenBreakdown — expandable per-model token usage detail.
 */

import { useState } from 'react';
import { Cpu, ChevronDown, ChevronRight } from 'lucide-react';
import { Text } from '../../atoms/Typography';
import { Button } from '../../atoms/Button';
import { formatTokens } from './utils';
import type { ExecutionStats } from './types';

export interface TokenBreakdownProps {
  stats: ExecutionStats;
  /** Override labels */
  labels?: Partial<Record<'tokenUsage' | 'in' | 'out', string>>;
}

export function TokenBreakdown({ stats, labels }: TokenBreakdownProps) {
  const [expanded, setExpanded] = useState(false);
  const hasModels = Object.keys(stats.tokenUsage.byModel).length > 0;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      <Button
        variant="ghost"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 h-auto bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 text-left"
      >
        <div className="flex items-center gap-2">
          <Cpu className="h-4 w-4 opacity-30" />
          <Text as="span" size="sm" weight="medium">{labels?.tokenUsage ?? 'Token Usage'}</Text>
        </div>
        <div className="flex items-center gap-2">
          <Text as="span" size="xs" color="muted">
            {formatTokens(stats.tokenUsage.totalInputTokens)} {labels?.in ?? 'in'} / {formatTokens(stats.tokenUsage.totalOutputTokens)} {labels?.out ?? 'out'}
          </Text>
          {hasModels && (
            expanded ? <ChevronDown className="h-3.5 w-3.5 opacity-40" /> : <ChevronRight className="h-3.5 w-3.5 opacity-40" />
          )}
        </div>
      </Button>
      {expanded && hasModels && (
        <div className="px-3 py-2 space-y-1.5 border-t border-slate-200 dark:border-slate-700">
          {Object.entries(stats.tokenUsage.byModel).map(([model, usage]) => (
            <div key={model} className="flex justify-between items-center">
              <Text size="xs" className="font-mono text-slate-500 dark:text-slate-400 truncate mr-2">{model}</Text>
              <Text size="xs" color="muted" className="flex-shrink-0">
                {formatTokens(usage.input)} / {formatTokens(usage.output)}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
